import React from 'react';
import { Box, Typography } from '@mui/material';
import { PlanStep, RequiredItemStatus, RequiredItemCategory, Participant, PlanRole, StepStatus } from '../../../types/generated/graphql';
import { StepPanelMode, getActiveDelegation } from '../../../utils/stepPanelMode';
import { colors } from '../../../theme/colors';
import { resolveStepAssignee } from '../../../utils/planHelpers';

interface StatusSummaryLineProps {
  step: PlanStep;
  panelMode: StepPanelMode;
  participants?: Participant[];
  roles?: PlanRole[];
}

/**
 * Compact single-line status display based on panel mode.
 * One accent color per mode: blue (active), amber (waiting), green (ready).
 *
 * Hidden for COMPLETED mode - completed steps show status in header chip
 * and CompletedContent component.
 */
export const StatusSummaryLine: React.FC<StatusSummaryLineProps> = ({
  step,
  panelMode,
  participants = [],
  roles = [],
}) => {
  // Don't show status line for completed steps - redundant with header
  if (panelMode === 'COMPLETED') {
    return null;
  }
  // Get progress info from assessment
  const assessment = step.assessment;
  const structured = assessment?.progress?.structured;

  // Count satisfied requirements
  const requiredItems = structured?.requiredItems?.filter(
    (i) => i.category === RequiredItemCategory.Required || !i.category
  ) || [];
  const satisfiedCount = requiredItems.filter(
    (i) => i.status === RequiredItemStatus.Done
  ).length;
  const totalRequired = requiredItems.length;

  // Get delegate info for WAITING mode
  const activeDelegation = getActiveDelegation(step);
  const delegateName = activeDelegation?.result?.fields?.name ||
    activeDelegation?.result?.summary?.split(' ')[0] ||
    'delegate';

  // Resolve assignee using role-based logic
  const assignment = resolveStepAssignee(step, roles, participants);
  const delegateDisplay = assignment.participant?.name ||
    (assignment.role ? `${assignment.role.name} (unassigned)` : delegateName);

  // Mode-specific rendering (ambient: just color and label)
  const getModeContent = () => {
    switch (panelMode) {
      case 'WAITING_ON_DELEGATE':
        return {
          label: `Waiting on ${delegateDisplay}`,
          color: colors.amber[600],
        };

      case 'READY_TO_COMPLETE':
        return {
          label: 'Ready to complete',
          color: colors.green[600],
        };

      case 'ACTIVE_WORK':
      default:
        // Show progress based on actual step status and assessment data
        let progressLabel: string;
        if (totalRequired > 0) {
          progressLabel = `${satisfiedCount}/${totalRequired} requirements`;
        } else if (step.status === StepStatus.NotStarted) {
          progressLabel = 'Ready to start';
        } else if (step.status === StepStatus.InProgress) {
          progressLabel = 'In progress';
        } else if (step.status === StepStatus.WaitingExternal) {
          const connectorType = (step.context as { connectorWait?: { connectorType?: string } })?.connectorWait?.connectorType;
          progressLabel = connectorType === 'foia' ? 'Waiting for agency response' : 'Waiting for response';
        } else if (step.status === StepStatus.Blocked) {
          progressLabel = 'Blocked';
        } else {
          progressLabel = 'Working';
        }

        return {
          label: progressLabel,
          color: step.status === StepStatus.Blocked ? colors.red[600] : step.status === StepStatus.WaitingExternal ? colors.amber[600] : colors.blue[600],
        };
    }
  };

  const content = getModeContent();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 0.5,
      }}
    >
      {/* Ambient status dot */}
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: content.color,
          flexShrink: 0,
        }}
      />
      <Typography
        variant="caption"
        sx={{
          color: colors.gray[500],
          fontWeight: 500,
        }}
      >
        {content.label}
      </Typography>

      {/* Show requirement details in ACTIVE_WORK mode if available */}
      {panelMode === 'ACTIVE_WORK' && totalRequired > 0 && satisfiedCount < totalRequired && (
        <Typography
          variant="caption"
          sx={{
            color: colors.gray[400],
            ml: 'auto',
          }}
        >
          {totalRequired - satisfiedCount} remaining
        </Typography>
      )}
    </Box>
  );
};

export default StatusSummaryLine;
